'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function PensionCalculator() {
  const t = useTranslations("tools.pensionCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentTab, setCurrentTab] = useState<'lumpsum' | 'survivor' | 'worklonger'>('lumpsum');
  const [showResults, setShowResults] = useState(false);

  // Lump Sum vs Monthly inputs
  const [lumpSumInputs, setLumpSumInputs] = useState({
    retirementAge: 65,
    lifeExpectancy: 85,
    lumpSum: 800000,
    investmentReturn: 5,
    monthlyPension: 5000,
    cola: 3.5,
  });

  // Survivor inputs
  const [survivorInputs, setSurvivorInputs] = useState({
    retirementAge: 65,
    lifeExpectancy: 77,
    spouseAge: 62,
    spouseLife: 82,
    singleLife: 5000,
    jointSurvivor: 3000,
    investmentReturn: 5,
    cola: 3.5,
  });

  // Work Longer inputs
  const [workLongerInputs, setWorkLongerInputs] = useState({
    retireAgeEarly: 60,
    pensionEarly: 2500,
    retireAgeLate: 65,
    pensionLate: 3800,
    lifeExpectancy: 85,
    investmentReturn: 5,
    cola: 3.5,
  });

  const [results, setResults] = useState<any>(null);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const styles = {
    container: getResponsiveStyle(
      { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
      { padding: '1rem' },
      { padding: '2rem' },
      { padding: '3rem' }
    ),
    header: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '2rem' },
      {},
      { marginBottom: '2.5rem' },
      { marginBottom: '3rem' }
    ),
    h1: getResponsiveStyle(
      { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
      { fontSize: '2rem' },
      { fontSize: '2.5rem' },
      { fontSize: '3rem' }
    ),
    summaryText: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    calculatorTool: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
        marginBottom: '2rem',
        borderRadius: '1rem',
      },
      { padding: '1.5rem 1rem' },
      { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
      { padding: '3rem 2rem', borderRadius: '1.5rem' }
    ),
    toolContainer: getResponsiveStyle(
      { margin: '0 auto' },
      { maxWidth: '100%' },
      { maxWidth: '600px' },
      { maxWidth: '800px' }
    ),
    toolHeader: getResponsiveStyle(
      { textAlign: 'center' as const, marginBottom: '1.5rem' },
      {},
      { marginBottom: '2rem' },
      { marginBottom: '2.5rem' }
    ),
    h2: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem', marginBottom: '1.25rem' },
      { fontSize: '2rem', marginBottom: '1.5rem' }
    ),
    toolSubtitle: getResponsiveStyle(
      { color: colors.textSecondary },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    ),
    calculatorTabs: getResponsiveStyle(
      { display: 'flex', gap: '1rem', marginBottom: '2rem', justifyContent: 'center' as const },
      { flexDirection: 'column' as const },
      { flexWrap: 'wrap' as const, flexDirection: 'row' as const },
      { flexWrap: 'wrap' as const, flexDirection: 'row' as const }
    ),
    tabBtn: (active: boolean) => getResponsiveStyle(
      {
        padding: '0.75rem 1.5rem',
        background: active ? 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)' : 'white',
        border: active ? 'none' : '2px solid #e2e8f0',
        borderRadius: '0.75rem',
        cursor: 'pointer',
        fontSize: '0.95rem',
        fontWeight: 600,
        color: active ? 'white' : colors.textSecondary,
        transition: 'all 0.3s ease',
      },
      { width: '100%' },
      {},
      {}
    ),
    calculatorForm: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    sectionTitle: getResponsiveStyle(
      {
        fontWeight: 700,
        color: colors.textPrimary,
        marginBottom: '1.5rem',
        paddingBottom: '0.75rem',
        borderBottom: '2px solid #e2e8f0',
      },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.5rem' }
    ),
    formGrid: getResponsiveStyle(
      { display: 'grid', gap: '1.5rem', marginBottom: '1.5rem' },
      { gridTemplateColumns: '1fr' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' },
      { gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))' }
    ),
    formGroup: {
      display: 'flex',
      flexDirection: 'column' as const,
    },
    label: getResponsiveStyle(
      { fontWeight: 600, color: '#334155', marginBottom: '0.5rem' },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    input: getResponsiveStyle(
      {
        padding: '0.75rem',
        border: '2px solid #e2e8f0',
        borderRadius: '0.5rem',
        fontSize: '1rem',
        transition: 'all 0.3s ease',
        width: '100%',
      },
      {},
      { padding: '0.875rem 1.25rem', fontSize: '1.125rem' },
      { padding: '1rem 1.5rem', fontSize: '1.25rem' }
    ),
    inputSuffix: {
      position: 'relative' as const,
    },
    suffix: {
      position: 'absolute' as const,
      right: '0.75rem',
      top: '50%',
      transform: 'translateY(-50%)',
      color: colors.textSecondary,
      fontSize: '0.9rem',
      pointerEvents: 'none' as const,
    },
    calculateBtn: getResponsiveStyle(
      {
        width: '100%',
        background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
        color: 'white',
        border: 'none',
        borderRadius: '0.75rem',
        fontWeight: 700,
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        marginTop: '1rem',
      },
      { padding: '0.875rem 1.5rem', fontSize: '1rem' },
      { padding: '1rem 2rem', fontSize: '1.125rem', borderRadius: '0.625rem' },
      { padding: '1.25rem 2.5rem', fontSize: '1.25rem', borderRadius: '0.75rem' }
    ),
    results: getResponsiveStyle(
      {
        background: 'white',
        borderRadius: '1rem',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    resultsTitle: getResponsiveStyle(
      { color: colors.textPrimary, marginBottom: '1.5rem', textAlign: 'center' as const },
      { fontSize: '1.5rem' },
      { fontSize: '1.75rem' },
      { fontSize: '2rem' }
    ),
    resultCard: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        borderRadius: '0.75rem',
        marginBottom: '1.5rem',
        borderLeft: '4px solid #2563eb',
      },
      { padding: '1rem' },
      { padding: '1.5rem', borderRadius: '1rem' },
      { padding: '2rem', borderRadius: '1.25rem' }
    ),
    resultCardTitle: getResponsiveStyle(
      { color: '#2563eb', marginBottom: '1rem', fontWeight: 700 },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.25rem' }
    ),
    resultItem: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '0.75rem 0',
      borderBottom: '1px solid #e2e8f0',
    },
    resultLabel: getResponsiveStyle(
      { color: colors.textSecondary, fontWeight: 500 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    resultValue: getResponsiveStyle(
      { color: colors.textPrimary, fontWeight: 700 },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.25rem' }
    ),
    resultValueHighlight: getResponsiveStyle(
      { color: '#2563eb', fontWeight: 700 },
      { fontSize: '1.1rem' },
      { fontSize: '1.2rem' },
      { fontSize: '1.5rem' }
    ),
    recommendation: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
        borderRadius: '0.75rem',
        borderLeft: '4px solid #22c55e',
        marginTop: '1.5rem',
      },
      { padding: '1rem' },
      { padding: '1.5rem', borderRadius: '1rem' },
      { padding: '2rem', borderRadius: '1.25rem' }
    ),
    recommendationTitle: getResponsiveStyle(
      { color: '#16a34a', marginBottom: '0.75rem', fontWeight: 700 },
      { fontSize: '1rem' },
      { fontSize: '1.1rem' },
      { fontSize: '1.25rem' }
    ),
    recommendationText: getResponsiveStyle(
      { color: '#166534', lineHeight: 1.6 },
      { fontSize: '0.9rem' },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' }
    ),
    warning: getResponsiveStyle(
      {
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '0.5rem',
        borderLeft: '4px solid #f59e0b',
        marginTop: '1rem',
        color: '#92400e',
      },
      { padding: '0.875rem', fontSize: '0.85rem' },
      { padding: '1rem', fontSize: '0.9rem' },
      { padding: '1.25rem', fontSize: '1rem' }
    ),
    glassCard: getResponsiveStyle(
      {
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(255, 200, 180, 0.2)',
        borderRadius: '1rem',
        boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
        marginBottom: '2rem',
      },
      { padding: '1.5rem' },
      { padding: '2rem', borderRadius: '1.25rem' },
      { padding: '2.5rem', borderRadius: '1.5rem' }
    ),
    paragraph: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6, marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem', lineHeight: 1.7 },
      { fontSize: '1.25rem', lineHeight: 1.8 }
    ),
    list: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.8, paddingLeft: '1.5rem', marginBottom: '1rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    listItem: {
      marginBottom: '0.75rem',
    },
    faqItem: {
      marginBottom: '1.5rem',
    },
    faqQuestion: getResponsiveStyle(
      { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' }
    ),
    faqAnswer: getResponsiveStyle(
      { color: colors.textSecondary, lineHeight: 1.6 },
      { fontSize: '0.95rem' },
      { fontSize: '1rem' },
      { fontSize: '1.125rem' }
    ),
    referenceLink: {
      color: colors.primary,
      textDecoration: 'none',
      wordBreak: 'break-word' as const,
    },
  };

  const formatNumber = (num: number) => {
    return num.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const calculateLumpSum = () => {
    const { retirementAge, lifeExpectancy, lumpSum, investmentReturn, monthlyPension, cola } = lumpSumInputs;
    const years = lifeExpectancy - retirementAge;
    const months = years * 12;
    const monthlyRate = investmentReturn / 100 / 12;
    const colaMonthly = cola / 100 / 12;

    let totalMonthlyPayments = 0;
    for (let i = 0; i < months; i++) {
      const adjustedPayment = monthlyPension * Math.pow(1 + colaMonthly, i);
      const presentValue = adjustedPayment / Math.pow(1 + monthlyRate, i);
      totalMonthlyPayments += presentValue;
    }

    const lumpSumFutureValue = lumpSum * Math.pow(1 + investmentReturn / 100, years);

    let monthlyTotal = 0;
    let currentPayment = monthlyPension;
    for (let i = 0; i < months; i++) {
      monthlyTotal += currentPayment;
      currentPayment *= (1 + colaMonthly);
    }

    const difference = totalMonthlyPayments - lumpSum;
    const percentDiff = (difference / lumpSum) * 100;

    return {
      type: 'lumpsum',
      lumpSum,
      lumpSumFutureValue,
      monthlyPension,
      monthlyTotal,
      totalMonthlyPayments,
      difference,
      percentDiff,
      years,
      investmentReturn,
      cola,
      lifeExpectancy,
    };
  };

  const calculateSurvivor = () => {
    const { retirementAge, lifeExpectancy, spouseAge, spouseLife, singleLife, jointSurvivor, investmentReturn, cola } = survivorInputs;
    const yourYears = lifeExpectancy - retirementAge;
    const spouseYearsAfterYou = spouseLife - (spouseAge + yourYears);
    const monthlyRate = investmentReturn / 100 / 12;
    const colaMonthly = cola / 100 / 12;

    let singleLifePV = 0;
    for (let i = 0; i < yourYears * 12; i++) {
      const payment = singleLife * Math.pow(1 + colaMonthly, i);
      singleLifePV += payment / Math.pow(1 + monthlyRate, i);
    }

    let jointPV = 0;
    const totalJointMonths = (spouseLife - spouseAge) * 12;
    for (let i = 0; i < totalJointMonths; i++) {
      const payment = jointSurvivor * Math.pow(1 + colaMonthly, i);
      jointPV += payment / Math.pow(1 + monthlyRate, i);
    }

    let singleLifeTotal = 0;
    let currentSingle = singleLife;
    for (let i = 0; i < yourYears * 12; i++) {
      singleLifeTotal += currentSingle;
      currentSingle *= (1 + colaMonthly);
    }

    let jointTotal = 0;
    let currentJoint = jointSurvivor;
    for (let i = 0; i < totalJointMonths; i++) {
      jointTotal += currentJoint;
      currentJoint *= (1 + colaMonthly);
    }

    const difference = jointPV - singleLifePV;
    const percentDiff = (difference / singleLifePV) * 100;

    return {
      type: 'survivor',
      singleLife,
      singleLifePV,
      singleLifeTotal,
      yourYears,
      lifeExpectancy,
      jointSurvivor,
      jointPV,
      jointTotal,
      spouseLife,
      spouseAge,
      difference,
      percentDiff,
    };
  };

  const calculateWorkLonger = () => {
    const { retireAgeEarly, pensionEarly, retireAgeLate, pensionLate, lifeExpectancy, investmentReturn, cola } = workLongerInputs;
    const yearsEarly = lifeExpectancy - retireAgeEarly;
    const yearsLate = lifeExpectancy - retireAgeLate;
    const extraWorkYears = retireAgeLate - retireAgeEarly;
    const monthlyRate = investmentReturn / 100 / 12;
    const colaMonthly = cola / 100 / 12;

    let earlyPV = 0;
    for (let i = 0; i < yearsEarly * 12; i++) {
      const payment = pensionEarly * Math.pow(1 + colaMonthly, i);
      earlyPV += payment / Math.pow(1 + monthlyRate, i);
    }

    let latePV = 0;
    const delayMonths = extraWorkYears * 12;
    for (let i = 0; i < yearsLate * 12; i++) {
      const payment = pensionLate * Math.pow(1 + colaMonthly, i);
      const discountPeriod = i + delayMonths;
      latePV += payment / Math.pow(1 + monthlyRate, discountPeriod);
    }

    let earlyTotal = 0;
    let currentEarly = pensionEarly;
    for (let i = 0; i < yearsEarly * 12; i++) {
      earlyTotal += currentEarly;
      currentEarly *= (1 + colaMonthly);
    }

    let lateTotal = 0;
    let currentLate = pensionLate;
    for (let i = 0; i < yearsLate * 12; i++) {
      lateTotal += currentLate;
      currentLate *= (1 + colaMonthly);
    }

    const difference = latePV - earlyPV;
    const breakEvenAge = retireAgeLate + (earlyPV - latePV) / (pensionLate * 12);

    return {
      type: 'worklonger',
      retireAgeEarly,
      pensionEarly,
      earlyPV,
      earlyTotal,
      yearsEarly,
      retireAgeLate,
      pensionLate,
      latePV,
      lateTotal,
      yearsLate,
      extraWorkYears,
      difference,
      breakEvenAge,
    };
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    let calculatedResults;

    if (currentTab === 'lumpsum') {
      calculatedResults = calculateLumpSum();
    } else if (currentTab === 'survivor') {
      calculatedResults = calculateSurvivor();
    } else {
      calculatedResults = calculateWorkLonger();
    }

    setResults(calculatedResults);
    setShowResults(true);
  };

  const renderLumpSumResults = () => {
    if (!results || results.type !== 'lumpsum') return null;

    return (
      <>
        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('option1LumpSum')}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('lumpSumAmount')}</span>
            <span style={styles.resultValue}>${formatNumber(results.lumpSum)}</span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('futureValue', { age: results.lifeExpectancy })}</span>
            <span style={styles.resultValue}>${formatNumber(results.lumpSumFutureValue)}</span>
          </div>
        </div>

        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('option2MonthlyPension')}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('monthlyPaymentStarting')}</span>
            <span style={styles.resultValue}>${formatNumber(results.monthlyPension)}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('totalPaymentsOverYears', { years: results.years })}</span>
            <span style={styles.resultValue}>${formatNumber(results.monthlyTotal)}</span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('presentValueOfPayments')}</span>
            <span style={styles.resultValueHighlight}>${formatNumber(results.totalMonthlyPayments)}</span>
          </div>
        </div>

        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('comparison')}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('differencePV')}</span>
            <span style={{ ...styles.resultValue, color: results.difference > 0 ? '#22c55e' : '#ef4444' }}>
              {results.difference > 0 ? '+' : ''}${formatNumber(results.difference)}
            </span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('percentageDifference')}</span>
            <span style={styles.resultValue}>{results.percentDiff > 0 ? '+' : ''}{results.percentDiff.toFixed(2)}%</span>
          </div>
        </div>

        <div style={styles.recommendation}>
          <h4 style={styles.recommendationTitle}>
            {results.difference > 0 ? t('recommendationMonthly') : t('recommendationLumpSum')}
          </h4>
          <p style={styles.recommendationText}>
            {results.difference > 0
              ? t('recommendationMonthlyText', {
                  pv: formatNumber(results.totalMonthlyPayments),
                  diff: formatNumber(results.difference),
                  percent: results.percentDiff.toFixed(1),
                })
              : t('recommendationLumpSumText', {
                  lumpSum: formatNumber(results.lumpSum),
                  diff: formatNumber(Math.abs(results.difference)),
                  percent: Math.abs(results.percentDiff).toFixed(1),
                  return: results.investmentReturn.toFixed(1),
                })}
          </p>
        </div>

        <div style={styles.warning}>
          {t('warningLumpSum', { return: results.investmentReturn.toFixed(1), cola: results.cola.toFixed(1) })}
        </div>
      </>
    );
  };

  const renderSurvivorResults = () => {
    if (!results || results.type !== 'survivor') return null;

    return (
      <>
        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('singleLifePensionOption')}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('monthlyPayment')}</span>
            <span style={styles.resultValue}>${formatNumber(results.singleLife)}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('paymentDuration')}</span>
            <span style={styles.resultValue}>{results.yourYears} {t('yearsUntilAge', { age: results.lifeExpectancy })}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('totalPayments')}</span>
            <span style={styles.resultValue}>${formatNumber(results.singleLifeTotal)}</span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('presentValue')}</span>
            <span style={styles.resultValueHighlight}>${formatNumber(results.singleLifePV)}</span>
          </div>
        </div>

        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('jointSurvivorPensionOption')}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('monthlyPayment')}</span>
            <span style={styles.resultValue}>${formatNumber(results.jointSurvivor)}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('paymentDuration')}</span>
            <span style={styles.resultValue}>
              {(results.spouseLife - results.spouseAge).toFixed(1)} {t('yearsUntilSpouseAge', { age: results.spouseLife })}
            </span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('totalPayments')}</span>
            <span style={styles.resultValue}>${formatNumber(results.jointTotal)}</span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('presentValue')}</span>
            <span style={styles.resultValueHighlight}>${formatNumber(results.jointPV)}</span>
          </div>
        </div>

        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('comparison')}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('differencePV')}</span>
            <span style={{ ...styles.resultValue, color: results.difference > 0 ? '#22c55e' : '#ef4444' }}>
              {results.difference > 0 ? '+' : ''}${formatNumber(results.difference)}
            </span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('monthlyPaymentReduction')}</span>
            <span style={styles.resultValue}>
              ${formatNumber(results.singleLife - results.jointSurvivor)} (
              {(((results.singleLife - results.jointSurvivor) / results.singleLife) * 100).toFixed(1)}%)
            </span>
          </div>
        </div>

        <div style={styles.recommendation}>
          <h4 style={styles.recommendationTitle}>
            {results.difference > 0 ? t('recommendationJoint') : t('recommendationSingle')}
          </h4>
          <p style={styles.recommendationText}>
            {results.difference > 0
              ? t('recommendationJointText', {
                  diff: formatNumber(Math.abs(results.difference)),
                  percent: Math.abs(results.percentDiff).toFixed(1),
                })
              : t('recommendationSingleText', {
                  diff: formatNumber(Math.abs(results.difference)),
                  percent: Math.abs(results.percentDiff).toFixed(1),
                })}
          </p>
        </div>

        <div style={styles.warning}>{t('warningSurvivor')}</div>
      </>
    );
  };

  const renderWorkLongerResults = () => {
    if (!results || results.type !== 'worklonger') return null;

    return (
      <>
        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('option1RetireAtAge', { age: results.retireAgeEarly })}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('monthlyPension')}</span>
            <span style={styles.resultValue}>${formatNumber(results.pensionEarly)}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('yearsOfPayments')}</span>
            <span style={styles.resultValue}>{results.yearsEarly} {t('years')}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('totalPayments')}</span>
            <span style={styles.resultValue}>${formatNumber(results.earlyTotal)}</span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('presentValue')}</span>
            <span style={styles.resultValueHighlight}>${formatNumber(results.earlyPV)}</span>
          </div>
        </div>

        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('option2WorkUntilAge', { age: results.retireAgeLate })}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('monthlyPension')}</span>
            <span style={styles.resultValue}>${formatNumber(results.pensionLate)}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('yearsOfPayments')}</span>
            <span style={styles.resultValue}>{results.yearsLate} {t('years')}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('totalPayments')}</span>
            <span style={styles.resultValue}>${formatNumber(results.lateTotal)}</span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('presentValue')}</span>
            <span style={styles.resultValueHighlight}>${formatNumber(results.latePV)}</span>
          </div>
        </div>

        <div style={styles.resultCard}>
          <h4 style={styles.resultCardTitle}>{t('analysis')}</h4>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('extraWorkYears')}</span>
            <span style={styles.resultValue}>{results.extraWorkYears} {t('years')}</span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('monthlyPensionIncrease')}</span>
            <span style={styles.resultValue}>
              ${formatNumber(results.pensionLate - results.pensionEarly)} (+
              {(((results.pensionLate - results.pensionEarly) / results.pensionEarly) * 100).toFixed(1)}%)
            </span>
          </div>
          <div style={styles.resultItem}>
            <span style={styles.resultLabel}>{t('presentValueDifference')}</span>
            <span style={{ ...styles.resultValue, color: results.difference > 0 ? '#22c55e' : '#ef4444' }}>
              {results.difference > 0 ? '+' : ''}${formatNumber(results.difference)}
            </span>
          </div>
          <div style={{ ...styles.resultItem, borderBottom: 'none' }}>
            <span style={styles.resultLabel}>{t('breakEvenAge')}</span>
            <span style={styles.resultValue}>{results.breakEvenAge > 0 ? results.breakEvenAge.toFixed(1) : 'N/A'}</span>
          </div>
        </div>

        <div style={styles.recommendation}>
          <h4 style={styles.recommendationTitle}>
            {results.difference > 0 ? t('recommendationWorkLonger') : t('recommendationEarlyRetirement')}
          </h4>
          <p style={styles.recommendationText}>
            {results.difference > 0
              ? t('recommendationWorkLongerText', {
                  age: results.retireAgeLate,
                  diff: formatNumber(Math.abs(results.difference)),
                  increase: formatNumber(results.pensionLate - results.pensionEarly),
                })
              : t('recommendationEarlyRetirementText', {
                  age: results.retireAgeEarly,
                  diff: formatNumber(Math.abs(results.difference)),
                  breakEven: results.breakEvenAge.toFixed(1),
                })}
          </p>
        </div>

        <div style={styles.warning}>{t('warningWorkLonger')}</div>
      </>
    );
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <header style={styles.header}>
        <h1 style={styles.h1}>{t('title')}</h1>
        <p style={styles.summaryText}>{t('subtitle')}</p>
      </header>

      {/* Calculator Tool */}
      <section style={styles.calculatorTool}>
        <div style={styles.toolContainer}>
          <div style={styles.toolHeader}>
            <h2 style={styles.h2}>{t('toolTitle')}</h2>
            <p style={styles.toolSubtitle}>{t('toolSubtitle')}</p>
          </div>

          <div style={styles.calculatorTabs}>
            <button
              style={styles.tabBtn(currentTab === 'lumpsum')}
              onClick={() => {
                setCurrentTab('lumpsum');
                setShowResults(false);
              }}
            >
              {t('tabLumpSum')}
            </button>
            <button
              style={styles.tabBtn(currentTab === 'survivor')}
              onClick={() => {
                setCurrentTab('survivor');
                setShowResults(false);
              }}
            >
              {t('tabSurvivor')}
            </button>
            <button
              style={styles.tabBtn(currentTab === 'worklonger')}
              onClick={() => {
                setCurrentTab('worklonger');
                setShowResults(false);
              }}
            >
              {t('tabWorkLonger')}
            </button>
          </div>

          <form style={styles.calculatorForm} onSubmit={handleCalculate}>
            {currentTab === 'lumpsum' && (
              <>
                <div style={styles.sectionTitle}>{t('lumpSumVsMonthly')}</div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('yourRetirementAge')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={lumpSumInputs.retirementAge}
                      onChange={(e) => setLumpSumInputs({ ...lumpSumInputs, retirementAge: parseFloat(e.target.value) })}
                      min="50"
                      max="80"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('lifeExpectancy')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={lumpSumInputs.lifeExpectancy}
                      onChange={(e) => setLumpSumInputs({ ...lumpSumInputs, lifeExpectancy: parseFloat(e.target.value) })}
                      min="60"
                      max="100"
                      required
                    />
                  </div>
                </div>

                <div style={{ ...styles.sectionTitle, marginTop: '2rem' }}>{t('option1LumpSumPayment')}</div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('lumpSumAmount')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={lumpSumInputs.lumpSum}
                        onChange={(e) => setLumpSumInputs({ ...lumpSumInputs, lumpSum: parseFloat(e.target.value) })}
                        min="0"
                        step="1000"
                        required
                      />
                      <span style={styles.suffix}>$</span>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('expectedInvestmentReturn')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={lumpSumInputs.investmentReturn}
                        onChange={(e) => setLumpSumInputs({ ...lumpSumInputs, investmentReturn: parseFloat(e.target.value) })}
                        min="0"
                        max="20"
                        step="0.1"
                        required
                      />
                      <span style={styles.suffix}>%</span>
                    </div>
                  </div>
                </div>

                <div style={{ ...styles.sectionTitle, marginTop: '2rem' }}>{t('option2MonthlyPensionPayment')}</div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('monthlyPensionIncome')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={lumpSumInputs.monthlyPension}
                        onChange={(e) => setLumpSumInputs({ ...lumpSumInputs, monthlyPension: parseFloat(e.target.value) })}
                        min="0"
                        step="100"
                        required
                      />
                      <span style={styles.suffix}>$</span>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('cola')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={lumpSumInputs.cola}
                        onChange={(e) => setLumpSumInputs({ ...lumpSumInputs, cola: parseFloat(e.target.value) })}
                        min="0"
                        max="10"
                        step="0.1"
                        required
                      />
                      <span style={styles.suffix}>%</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {currentTab === 'survivor' && (
              <>
                <div style={styles.sectionTitle}>{t('singleLifeVsJointSurvivor')}</div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('yourRetirementAge')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={survivorInputs.retirementAge}
                      onChange={(e) => setSurvivorInputs({ ...survivorInputs, retirementAge: parseFloat(e.target.value) })}
                      min="50"
                      max="80"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('yourLifeExpectancy')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={survivorInputs.lifeExpectancy}
                      onChange={(e) => setSurvivorInputs({ ...survivorInputs, lifeExpectancy: parseFloat(e.target.value) })}
                      min="60"
                      max="100"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('spouseAgeAtRetirement')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={survivorInputs.spouseAge}
                      onChange={(e) => setSurvivorInputs({ ...survivorInputs, spouseAge: parseFloat(e.target.value) })}
                      min="40"
                      max="80"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('spouseLifeExpectancy')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={survivorInputs.spouseLife}
                      onChange={(e) => setSurvivorInputs({ ...survivorInputs, spouseLife: parseFloat(e.target.value) })}
                      min="60"
                      max="100"
                      required
                    />
                  </div>
                </div>

                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('singleLifePension')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={survivorInputs.singleLife}
                        onChange={(e) => setSurvivorInputs({ ...survivorInputs, singleLife: parseFloat(e.target.value) })}
                        min="0"
                        step="100"
                        required
                      />
                      <span style={styles.suffix}>$/mo</span>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('jointSurvivorPension')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={survivorInputs.jointSurvivor}
                        onChange={(e) => setSurvivorInputs({ ...survivorInputs, jointSurvivor: parseFloat(e.target.value) })}
                        min="0"
                        step="100"
                        required
                      />
                      <span style={styles.suffix}>$/mo</span>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('expectedInvestmentReturn')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={survivorInputs.investmentReturn}
                        onChange={(e) => setSurvivorInputs({ ...survivorInputs, investmentReturn: parseFloat(e.target.value) })}
                        min="0"
                        max="20"
                        step="0.1"
                        required
                      />
                      <span style={styles.suffix}>%</span>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('cola')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={survivorInputs.cola}
                        onChange={(e) => setSurvivorInputs({ ...survivorInputs, cola: parseFloat(e.target.value) })}
                        min="0"
                        max="10"
                        step="0.1"
                        required
                      />
                      <span style={styles.suffix}>%</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            {currentTab === 'worklonger' && (
              <>
                <div style={styles.sectionTitle}>{t('shouldYouWorkLonger')}</div>

                <div style={{ ...styles.sectionTitle, marginTop: '1.5rem', fontSize: '1.1rem' }}>{t('option1RetireEarlier')}</div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('retirementAge')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={workLongerInputs.retireAgeEarly}
                      onChange={(e) => setWorkLongerInputs({ ...workLongerInputs, retireAgeEarly: parseFloat(e.target.value) })}
                      min="50"
                      max="75"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('monthlyPensionIncome')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={workLongerInputs.pensionEarly}
                        onChange={(e) => setWorkLongerInputs({ ...workLongerInputs, pensionEarly: parseFloat(e.target.value) })}
                        min="0"
                        step="100"
                        required
                      />
                      <span style={styles.suffix}>$/mo</span>
                    </div>
                  </div>
                </div>

                <div style={{ ...styles.sectionTitle, marginTop: '1.5rem', fontSize: '1.1rem' }}>{t('option2WorkLongerOption')}</div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('retirementAge')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={workLongerInputs.retireAgeLate}
                      onChange={(e) => setWorkLongerInputs({ ...workLongerInputs, retireAgeLate: parseFloat(e.target.value) })}
                      min="50"
                      max="75"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('monthlyPensionIncome')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={workLongerInputs.pensionLate}
                        onChange={(e) => setWorkLongerInputs({ ...workLongerInputs, pensionLate: parseFloat(e.target.value) })}
                        min="0"
                        step="100"
                        required
                      />
                      <span style={styles.suffix}>$/mo</span>
                    </div>
                  </div>
                </div>

                <div style={{ ...styles.sectionTitle, marginTop: '1.5rem', fontSize: '1.1rem' }}>{t('otherInformation')}</div>
                <div style={styles.formGrid}>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('lifeExpectancy')}</label>
                    <input
                      type="number"
                      style={styles.input}
                      value={workLongerInputs.lifeExpectancy}
                      onChange={(e) => setWorkLongerInputs({ ...workLongerInputs, lifeExpectancy: parseFloat(e.target.value) })}
                      min="60"
                      max="100"
                      required
                    />
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('expectedInvestmentReturn')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={workLongerInputs.investmentReturn}
                        onChange={(e) => setWorkLongerInputs({ ...workLongerInputs, investmentReturn: parseFloat(e.target.value) })}
                        min="0"
                        max="20"
                        step="0.1"
                        required
                      />
                      <span style={styles.suffix}>%</span>
                    </div>
                  </div>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>{t('cola')}</label>
                    <div style={styles.inputSuffix}>
                      <input
                        type="number"
                        style={{ ...styles.input, paddingRight: '3.5rem' }}
                        value={workLongerInputs.cola}
                        onChange={(e) => setWorkLongerInputs({ ...workLongerInputs, cola: parseFloat(e.target.value) })}
                        min="0"
                        max="10"
                        step="0.1"
                        required
                      />
                      <span style={styles.suffix}>%</span>
                    </div>
                  </div>
                </div>
              </>
            )}

            <button type="submit" style={styles.calculateBtn}>
              {t('calculateButton')}
            </button>
          </form>

          {showResults && (
            <div style={styles.results}>
              <h3 style={styles.resultsTitle}>{t('calculationResults')}</h3>
              {currentTab === 'lumpsum' && renderLumpSumResults()}
              {currentTab === 'survivor' && renderSurvivorResults()}
              {currentTab === 'worklonger' && renderWorkLongerResults()}
            </div>
          )}
        </div>
      </section>

      {/* Intro Section */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('introTitle')}</h2>
        <p style={styles.paragraph}>{t('introParagraph1')}</p>
        <p style={styles.paragraph}>{t('introParagraph2')}</p>
        <p style={styles.paragraph}>{t('introParagraph3')}</p>
      </section>

      {/* How to Use */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('howToUseTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>{t('howToUseStep1')}</li>
          <li style={styles.listItem}>{t('howToUseStep2')}</li>
          <li style={styles.listItem}>{t('howToUseStep3')}</li>
          <li style={styles.listItem}>{t('howToUseStep4')}</li>
          <li style={styles.listItem}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      {/* Insights */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('insightsTitle')}</h2>
        <p style={styles.paragraph}>{t('insightsParagraph1')}</p>
        <ul style={styles.list}>
          <li style={styles.listItem}>{t('insightsPoint1')}</li>
          <li style={styles.listItem}>{t('insightsPoint2')}</li>
          <li style={styles.listItem}>{t('insightsPoint3')}</li>
          <li style={styles.listItem}>{t('insightsPoint4')}</li>
          <li style={styles.listItem}>{t('insightsPoint5')}</li>
        </ul>
      </section>

      {/* Details */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('detailsTitle')}</h2>
        <p style={styles.paragraph}>{t('detailsParagraph1')}</p>
        <p style={styles.paragraph}>{t('detailsParagraph2')}</p>
        <p style={styles.paragraph}>{t('detailsParagraph3')}</p>
        <p style={styles.paragraph}>{t('detailsParagraph4')}</p>
      </section>

      {/* FAQ */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('faqTitle')}</h2>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion1')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer1')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion2')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer2')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion3')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer3')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion4')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer4')}</p>
        </div>
        <div style={styles.faqItem}>
          <h3 style={styles.faqQuestion}>{t('faqQuestion5')}</h3>
          <p style={styles.faqAnswer}>{t('faqAnswer5')}</p>
        </div>
      </section>

      {/* References */}
      <section style={styles.glassCard}>
        <h2 style={styles.h2}>{t('referencesTitle')}</h2>
        <ol style={styles.list}>
          <li style={styles.listItem}>
            <a href="https://saberpension.com/defined-benefit-plan-calculator" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t('reference1')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://smartasset.com/retirement/retirement-calculator" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t('reference2')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.ssa.gov/oact/anypia/anypia.html" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t('reference3')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.empower.com/calculators/pension" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t('reference4')}
            </a>
          </li>
          <li style={styles.listItem}>
            <a href="https://www.schwabmoneywise.com/monthly-pension-vs-lump-sum-payout-calculator" style={styles.referenceLink} target="_blank" rel="noopener noreferrer">
              {t('reference5')}
            </a>
          </li>
        </ol>
      </section>
    </div>
  );
}